import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ValidationHandler } from 'app/helpers/validation.handler';
import { AuthType, ConnectedAppAuth, ConnectedAppAuthScope, Oauth2GrantType } from 'app/models/connected-app';
import { ConnectedAppService } from 'app/services/connected-app.service';
import { ModalDirective, ToastService } from 'ng-uikit-pro-standard';
import { forkJoin, Observable, of } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';

@Component({
  selector: 'app-connected-app-authorize',
  templateUrl: './connected-app-authorize.component.html',
  styleUrls: ['./connected-app-authorize.component.scss']
})
export class ConnectedAppAuthorizeComponent implements OnInit {

  connectedAppAuths: ConnectedAppAuth[] = [];
  authsFormGroup: FormGroup[] = [];

  isGlobal: boolean = true;
  showOnlyUnAuthorized: boolean = true;
  isLoading: boolean = false;
  loadingIndices: number[] = [];
  
  @Output() allAuthorized: EventEmitter<null> = new EventEmitter();

  /** @todo add option to auto close on all authenticated and trigger event */
  /** @todo add event when cancelled and some not authenticated or a completed event with authentication status */
  /** @todo option to hide scopes */
  
  @ViewChild('authorizeModal', { static: true }) authorizeModal: ModalDirective;

  constructor(private connectedAppService: ConnectedAppService, private formBuilder: FormBuilder,
    private validationHandler: ValidationHandler, private toastService: ToastService) { }

  ngOnInit(): void {
  }

  buildFormGroups() {
    this.authsFormGroup = [];
    this.connectedAppAuths.forEach((c) => {
      if (c.type == AuthType.BASIC || (c.type == AuthType.OAUTH2 && c.oauth2_grant_type == Oauth2GrantType.PASSWORD)) {
        this.authsFormGroup.push(this.formBuilder.group({
          connected_app_auth_id: c.id,
          auth_info: this.formBuilder.group({
            username: [null, Validators.required],
            password: [null, Validators.required]
          }),
          oauth2_scopes: [c.type == AuthType.OAUTH2? c.oauth2_scopes: null]
        }));
      } else if (c.type == AuthType.API_KEY) {
        this.authsFormGroup.push(this.formBuilder.group({
          connected_app_auth_id: c.id,
          auth_info: this.formBuilder.group({
            api_key: [null, Validators.required],
          })
        }));
      } else if (c.type == AuthType.OAUTH2) {
        if (c.oauth2_grant_type == Oauth2GrantType.IMPLICIT) {
          this.authsFormGroup.push(this.formBuilder.group({
            connected_app_auth_id: c.id,
            auth_info: this.formBuilder.group({
              access_token: [null, Validators.required],
              valid_before: [null, Validators.required]
            }),
            oauth2_scopes: [c.oauth2_scopes]
          }));
        } else if (c.oauth2_grant_type == Oauth2GrantType.AUTHORIZATION_CODE) {
          this.authsFormGroup.push(this.formBuilder.group({
            connected_app_auth_id: c.id,
            auth_info: this.formBuilder.group({
              code: [null, Validators.required],
              redirect_uri: [this.connectedAppService.getAuthRedirectUrl(c, this.isGlobal), Validators.required]
            }),
            oauth2_scopes: [c.oauth2_scopes]
          }));
        } else if (c.oauth2_grant_type == Oauth2GrantType.CLIENT_CREDENTIALS) {
          this.authsFormGroup.push(this.formBuilder.group({}));
        }
      }
    });
  }

  show(connectedAppAuths: ConnectedAppAuth[], isGlobal: boolean = true, showOnlyUnAuthorized: boolean = false) {
    this.showOnlyUnAuthorized = showOnlyUnAuthorized;
    this.isGlobal = isGlobal;
    this.connectedAppAuths = connectedAppAuths;
    this.buildFormGroups();
    this.isLoading = true;
    this.isAuthorized(connectedAppAuths, isGlobal).pipe(finalize(() => this.isLoading = false)).subscribe();
    this.authorizeModal.show();
  }

  showIfNeedsAuthorization(connectedAppAuths: ConnectedAppAuth[], isGlobal: boolean = true): Observable<boolean> {
    return this.isAuthorized(connectedAppAuths, isGlobal).pipe(tap((n) => {
      if (!n) {
        this.showOnlyUnAuthorized = true;
        this.isGlobal = isGlobal;
        this.connectedAppAuths = connectedAppAuths;
        this.buildFormGroups();
        this.authorizeModal.show();
      }
    }));
  }

  isAuthorized(connectedAppAuths: ConnectedAppAuth[], isGlobal: boolean = true) : Observable<boolean> {
    this.connectedAppAuths = connectedAppAuths;
    this.isGlobal = isGlobal;
    if (this.connectedAppAuths.length == 0) {
      return of(true);
    }

    let observablesArray: Observable<boolean>[] = [];
    this.connectedAppAuths.forEach((a) => {
      observablesArray.push(this.connectedAppService.isAuthAuthenticated(a, this.isGlobal).pipe(tap((isAuthenticated) => {
        this.connectedAppAuths.find((c) => c.id == a.id).is_authenticated = isAuthenticated;
      })));
    });

    return forkJoin(observablesArray).pipe(map((result) => {
      let isAllAuthenticated: boolean = true;
      result.forEach((v) => {
        isAllAuthenticated = isAllAuthenticated && v;
      });
      return isAllAuthenticated;
    }));
  }

  submitAll() {
    this.connectedAppAuths.forEach((a, i) => {
      if (a.is_authenticated == false) {
        this.submitAuth(i);
      }
    });
  }
  submitAuth(i: number) {
    this.validationHandler.onSubmitCheck(this.authsFormGroup[i]);
    if(!this.authsFormGroup[i].valid) {
      return;
    }

    this.loadingIndices.push(i);

    this.connectedAppService.activateAuthentication(this.authsFormGroup[i].value, this.isGlobal).pipe(finalize(() => {
      this.loadingIndices = this.loadingIndices.filter((e) => e != i);
    })).subscribe(() => {
      this.connectedAppAuths[i].is_authenticated = true;
      this.checkIfAllAuthorized();
    }, (err) => {
      this.toastService.error(`Could not authenticate: ${this.connectedAppAuths[i].name}`);
      console.log(err);
    });
  }

  checkIfAllAuthorized() {
    let isAllAuthenticated: boolean = true;
    this.connectedAppAuths.forEach((a) => {
      isAllAuthenticated = isAllAuthenticated && a.is_authenticated;
    });

    if (isAllAuthenticated) {
      if (this.showOnlyUnAuthorized && this.authorizeModal.isShown) {
        this.authorizeModal.hide();
      }

      this.allAuthorized.emit();
    }
    return isAllAuthenticated;
  }
  
  startOauthFlow(i: number) {
    let redirectUrl : string = this.connectedAppService.getAuthRedirectUrl(this.connectedAppAuths[i], this.isGlobal);
    let authorizeUrl: string = this.connectedAppAuths[i].oauth2_authorization_url;
    let responseType: string = this.connectedAppAuths[i].oauth2_grant_type == Oauth2GrantType.AUTHORIZATION_CODE? 'code': 'token';
    let scope: string = this.authsFormGroup[i].get('oauth2_scopes').value? (<ConnectedAppAuthScope[]>this.authsFormGroup[i].get('oauth2_scopes').value).map((a) => a.name).join(' '): '';

    authorizeUrl = `${authorizeUrl}/?response_type=${responseType}&client_id=${this.connectedAppAuths[i].oauth2_client_id}&redirect_uri=${encodeURIComponent(redirectUrl)}&scope=${encodeURIComponent(scope)}&state=ABC`;

    localStorage.removeItem('ecodocs_auth_' + this.connectedAppAuths[i].id);
    window.addEventListener('storage', (e) => {
      if (e.key == 'ecodocs_auth_' + this.connectedAppAuths[i].id && e.newValue) {
        try {
          let auth_info = JSON.parse(e.newValue);
          auth_info.redirect_uri = redirectUrl;
          this.authsFormGroup[i].get('auth_info').setValue(auth_info);
        } catch (error) {
          console.log(error);
        }
      }
    });
    window.open(authorizeUrl, '_blank');
  }
}
